/**
 * PostgreSQL dashboard templates (S7, remediated S7-R). SQL adapted from the
 * same catalog views the Session Browser (`electron/session-browser/providers/pgsqlSessionQueries.ts`)
 * and Performance Dashboard (`electron/performance-dashboard/providers/pgsqlPerformanceDashboardQueries.ts`)
 * already query in production.
 */
import type { DashboardTemplate } from '../templateTypes';

export const pgsqlTemplates: DashboardTemplate[] = [
  {
    id: 'pgsql.sessions.active_queries',
    engine: 'pgsql',
    title: 'Active queries',
    description: 'Currently running statements, oldest first — the first thing to check when the database feels slow. Full detail on other sessions requires pg_monitor / pg_read_all_stats (own session is always visible).',
    category: 'sessions',
    fits: ['table'],
    defaultTileType: 'table',
    expectedShape: 'rows',
    emptyStateCopy: 'No active queries right now.',
    sql: `SELECT
  pid,
  usename,
  datname,
  state,
  wait_event_type,
  wait_event,
  now() - query_start AS running_for,
  LEFT(query, 500) AS query_text
FROM pg_stat_activity
WHERE pid <> pg_backend_pid()
  AND state = 'active'
  AND {{dashboard_filters}}
ORDER BY query_start ASC`,
  },
  {
    id: 'pgsql.sessions.lock_waits',
    engine: 'pgsql',
    title: 'Lock waits',
    description: 'Sessions waiting on a lock they have not been granted — catch a stuck transaction before it queues up the rest. Full detail on other sessions requires pg_monitor / pg_read_all_stats (own session is always visible).',
    category: 'sessions',
    fits: ['table'],
    defaultTileType: 'table',
    expectedShape: 'rows',
    emptyStateCopy: 'No sessions are currently waiting on a lock.',
    sql: `SELECT
  l.pid,
  a.usename,
  l.locktype,
  l.mode,
  l.granted,
  c.relname AS relation
FROM pg_locks l
JOIN pg_stat_activity a ON l.pid = a.pid
LEFT JOIN pg_class c ON l.relation = c.oid
WHERE NOT l.granted
  AND {{dashboard_filters}}
ORDER BY l.pid`,
  },
  {
    id: 'pgsql.sessions.idle_in_transaction',
    engine: 'pgsql',
    title: 'Idle-in-transaction / longest transactions',
    description: 'Sessions holding an open transaction, oldest first — an idle-in-transaction session pins autovacuum and holds locks indefinitely. Full detail on other sessions requires pg_monitor / pg_read_all_stats (own session is always visible).',
    category: 'sessions',
    fits: ['table'],
    defaultTileType: 'table',
    expectedShape: 'rows',
    emptyStateCopy: 'No sessions are currently holding an open transaction.',
    sql: `SELECT
  pid,
  usename,
  datname,
  state,
  now() - xact_start AS transaction_age,
  LEFT(query, 500) AS query_text
FROM pg_stat_activity
WHERE xact_start IS NOT NULL
  AND pid <> pg_backend_pid()
  AND {{dashboard_filters}}
ORDER BY xact_start ASC`,
  },
  {
    id: 'pgsql.performance.top_waits',
    engine: 'pgsql',
    title: 'Top wait events',
    description: 'What active backends are waiting on right now — the dominant wait event tells you what the engine is starving on. Full detail on other sessions requires pg_monitor / pg_read_all_stats (own session is always visible).',
    category: 'performance',
    fits: ['chart', 'table'],
    defaultTileType: 'chart',
    expectedShape: 'rows',
    emptyStateCopy: 'No backends are currently waiting on anything.',
    sql: `SELECT
  wait_event_type,
  wait_event,
  count(*) AS session_count
FROM pg_stat_activity
WHERE wait_event IS NOT NULL
  AND pid <> pg_backend_pid()
  AND {{dashboard_filters}}
GROUP BY wait_event_type, wait_event
ORDER BY session_count DESC`,
  },
  {
    id: 'pgsql.performance.cache_hit_ratio',
    engine: 'pgsql',
    title: 'Cache hit ratio',
    description: 'Share of block reads served from shared buffers, instance-wide — a sustained drop means memory pressure is pushing reads to disk.',
    category: 'performance',
    fits: ['stat'],
    defaultTileType: 'stat',
    statValueColumn: 'cache_hit_ratio_pct',
    expectedShape: 'scalar',
    emptyStateCopy: 'No database statistics available yet.',
    sql: `SELECT
  round(100.0 * sum(blks_hit) / NULLIF(sum(blks_hit) + sum(blks_read), 0), 2) AS cache_hit_ratio_pct
FROM pg_stat_database
WHERE {{dashboard_filters}}`,
  },
  {
    id: 'pgsql.performance.connection_saturation',
    engine: 'pgsql',
    title: 'Connection saturation',
    description: 'Client connections as a share of max_connections — approaching 100% means new connections start getting refused; time for a pooler or a higher limit. Reserved slots (superuser_reserved_connections, plus reserved_connections on PG16+) are held back for privileged roles, so ordinary connections can be refused below 100%.',
    category: 'performance',
    fits: ['stat'],
    defaultTileType: 'stat',
    statValueColumn: 'connections_used_pct',
    expectedShape: 'scalar',
    // count(*) over pg_stat_activity ALWAYS returns exactly one row — "0
    // rows" can never happen (same shape note as pg_stat_archiver above).
    emptyStateCopy: 'Always returns one row — the share of max_connections currently used by client backends.',
    sql: `SELECT
  count(*) AS client_connections,
  current_setting('max_connections')::int AS max_connections,
  round(100.0 * count(*) / current_setting('max_connections')::int, 1) AS connections_used_pct
FROM pg_stat_activity
WHERE backend_type = 'client backend'
  AND {{dashboard_filters}}`,
  },
  {
    id: 'pgsql.performance.long_running_queries',
    engine: 'pgsql',
    title: 'Queries running over a minute',
    description: 'Active statements that have been running for more than a minute, oldest first — the usual suspects when everything else queues up behind them. Full detail on other sessions requires pg_monitor / pg_read_all_stats (own session is always visible).',
    category: 'performance',
    fits: ['table'],
    defaultTileType: 'table',
    expectedShape: 'rows',
    emptyStateCopy: 'No queries have been running for more than a minute.',
    sql: `SELECT
  pid,
  usename,
  datname,
  now() - query_start AS running_for,
  wait_event_type,
  LEFT(query, 500) AS query_text
FROM pg_stat_activity
WHERE state = 'active'
  AND backend_type = 'client backend'
  AND pid <> pg_backend_pid()
  AND query_start < now() - interval '1 minute'
  AND {{dashboard_filters}}
ORDER BY query_start ASC`,
  },
  {
    id: 'pgsql.storage.database_sizes',
    engine: 'pgsql',
    title: 'Database sizes',
    description: 'On-disk size per database — spot the one growing faster than disk was budgeted for. pg_database_size() requires CONNECT privilege on each database being measured (or superuser).',
    category: 'storage',
    fits: ['chart', 'table'],
    defaultTileType: 'chart',
    expectedShape: 'rows',
    emptyStateCopy: 'No databases found on this server.',
    sql: `SELECT
  datname AS database_name,
  pg_database_size(datname) / 1024 / 1024 AS size_mb
FROM pg_database
WHERE datistemplate = false
  AND {{dashboard_filters}}
ORDER BY size_mb DESC`,
  },
  {
    id: 'pgsql.storage.dead_tuple_bloat',
    engine: 'pgsql',
    title: 'Dead tuple pressure',
    description: 'Tables with the highest estimated share of dead tuples — the ones autovacuum is falling behind on. n_dead_tup is an estimate, not a physical bloat measurement.',
    category: 'storage',
    fits: ['table', 'chart'],
    defaultTileType: 'table',
    expectedShape: 'rows',
    emptyStateCopy: 'No tables with significant dead-tuple bloat.',
    sql: `SELECT
  schemaname,
  relname AS table_name,
  n_live_tup,
  n_dead_tup,
  round(100.0 * n_dead_tup / NULLIF(n_live_tup + n_dead_tup, 0), 1) AS dead_pct
FROM pg_stat_user_tables
WHERE n_dead_tup > 1000
  AND {{dashboard_filters}}
ORDER BY dead_pct DESC`,
  },
  {
    id: 'pgsql.indexes.unused_indexes',
    engine: 'pgsql',
    title: 'Unused indexes',
    description: 'Non-unique, non-primary-key indexes unused since statistics were last reset (pg_stat_user_indexes.stats_reset / pg_stat_reset()) — worth dropping after you confirm, not proof they are dead forever.',
    category: 'indexes',
    fits: ['table'],
    defaultTileType: 'table',
    expectedShape: 'rows',
    emptyStateCopy: 'Every index has been scanned at least once since the last statistics reset.',
    sql: `SELECT
  s.schemaname,
  s.relname AS table_name,
  s.indexrelname AS index_name,
  s.idx_scan,
  pg_relation_size(s.indexrelid) / 1024 / 1024 AS size_mb
FROM pg_stat_user_indexes s
JOIN pg_index i ON i.indexrelid = s.indexrelid
WHERE s.idx_scan = 0
  AND NOT i.indisprimary
  AND NOT i.indisunique
  AND {{dashboard_filters}}
ORDER BY size_mb DESC`,
  },
  {
    id: 'pgsql.indexes.seq_scan_hotspots',
    engine: 'pgsql',
    title: 'Sequential scan hotspots (by scan count)',
    description: 'Tables whose reads are mostly sequential-scan EXECUTIONS — each scan counts once, so a billion-row seq scan and a tiny point lookup weigh the same; check n_live_tup for table size. A high count share on a big table is the classic missing-index signal (small tables are routinely seq-scanned by design).',
    category: 'indexes',
    fits: ['table', 'chart'],
    defaultTileType: 'table',
    expectedShape: 'rows',
    emptyStateCopy: 'No sequential scans recorded since the last statistics reset.',
    sql: `SELECT
  schemaname,
  relname AS table_name,
  seq_scan,
  COALESCE(idx_scan, 0) AS idx_scan,
  round(100.0 * seq_scan / NULLIF(seq_scan + COALESCE(idx_scan, 0), 0), 1) AS seq_scan_count_pct,
  n_live_tup,
  seq_tup_read
FROM pg_stat_user_tables
WHERE seq_scan > 0
  AND {{dashboard_filters}}
ORDER BY seq_tup_read DESC`,
  },
  {
    id: 'pgsql.indexes.invalid_indexes',
    engine: 'pgsql',
    title: 'Invalid indexes',
    description: 'Indexes currently invalid — usually left behind by a failed CREATE INDEX or REINDEX CONCURRENTLY, but possibly a concurrent build still in progress; investigate if it persists. indisready = true means every write still maintains the index; false means writes ignore it entirely (indislive = false marks it mid-drop).',
    category: 'indexes',
    fits: ['table'],
    defaultTileType: 'table',
    expectedShape: 'rows',
    emptyStateCopy: 'No invalid indexes in this database.',
    sql: `SELECT
  n.nspname AS schema_name,
  t.relname AS table_name,
  i.relname AS index_name,
  ix.indisready,
  ix.indislive,
  pg_relation_size(ix.indexrelid) / 1024 / 1024 AS size_mb
FROM pg_index ix
JOIN pg_class i ON i.oid = ix.indexrelid
JOIN pg_class t ON t.oid = ix.indrelid
JOIN pg_namespace n ON n.oid = i.relnamespace
WHERE NOT ix.indisvalid
  AND {{dashboard_filters}}
ORDER BY size_mb DESC`,
  },
  {
    id: 'pgsql.backup.wal_archive_failures',
    engine: 'pgsql',
    title: 'WAL archive failures',
    description: 'Failed WAL-archiving attempts in the last 24 hours — a broken archive means no point-in-time recovery past the last good segment. Requires WAL archiving to be enabled.',
    category: 'backup',
    fits: ['stat'],
    defaultTileType: 'stat',
    statValueColumn: 'wal_archive_failures_recent',
    expectedShape: 'scalar',
    // pg_stat_archiver ALWAYS returns exactly one row (even with archiving
    // off, values are just 0/null) — "0 rows" can never happen (M23/S3).
    emptyStateCopy: 'Always returns one row — 0 recent failures means archiving is healthy; a non-zero count needs investigation.',
    sql: `SELECT
  CASE WHEN last_failed_time > now() - interval '24 hours' THEN failed_count ELSE 0 END AS wal_archive_failures_recent,
  last_failed_time
FROM pg_stat_archiver
WHERE {{dashboard_filters}}`,
  },
  {
    id: 'pgsql.performance.lock_wait_stats',
    engine: 'pgsql',
    title: 'Lock wait stats (pg_stat_lock)',
    description: 'Per-lock-type wait counts and total wait time since the last stats reset — a PostgreSQL 19+ view, richer than pg_locks alone.',
    category: 'performance',
    fits: ['table', 'chart'],
    defaultTileType: 'table',
    // PG19-FACTS: pg_stat_lock is a PostgreSQL 19+ view (querying it directly
    // on an older server raises 42P01) — this template exercises the
    // version-gate, now backed by a live server-version probe (S7-R M1).
    minimumVersion: '19',
    expectedShape: 'rows',
    emptyStateCopy: 'No recorded lock waits since the last stats reset.',
    sql: `SELECT
  locktype,
  waits,
  wait_time,
  fastpath_exceeded
FROM pg_stat_lock
WHERE waits > 0
  AND {{dashboard_filters}}
ORDER BY wait_time DESC`,
  },
];
